

import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import moment from 'moment';
import Promoter from '@/models/Promoter';
import { promoterStoreActions } from '@/store/promoter.store';
import PromoterStatistics from '@/models/PromoterStatistics';
import Printd from 'printd';

const PromoterStore = namespace('promoter');

@Component
export default class PromoterStatisticsListComponent extends Vue {
    @Prop({ required: true })
    private promoter!: Promoter | null;

    @PromoterStore.Action(promoterStoreActions.STATISTICS)
    public getStatistics!: (id: string) => Promise<PromoterStatistics>;

    /**
     * PrintD Instance to print a given element
     */
    private printer: Printd = new Printd();

    private printStyle: string = `
    .headline {
      font-size: 24px;
      font-weight: 700;
      font-family: "Calibri", "Arial", "sans-serif";
    }

    .colorized {
        color: #E97C38;
    }

    .font-style {
        font-family: "Calibri", "Arial", "sans-serif";
    }

    h3 {
       margin-bottom: 4px;
    }

    .font-weight-bold {
       font-weight: 700;
    }

    .hide-on-print {
        display: none;
    }

    table {
        border-collapse: collapse;
        width: 100%;
    }

    th, td {
        text-align: left;
        padding: 4px 8px;
        border-bottom: 1px solid #ddd;
    }
  `;

    public isLoading: boolean = false;
    public statistics: PromoterStatistics | null = null;
    public search: string = '';
    public itemsPerPage: number = 25;

    public get headers() {
        return [
            { text: this.$t('PROMOTER.STATISTICS.TABLE.DRIVER'), value: 'name', sortable: true },
            { text: this.$t('PROMOTER.STATISTICS.TABLE.SERIAL'), value: 'serialId', sortable: true },
            { text: this.$t('PROMOTER.STATISTICS.TABLE.COMPANY'), value: 'company', sortable: true },
            { text: this.$t('PROMOTER.STATISTICS.TABLE.STATUS'), value: 'status', sortable: true },
            { text: this.$t('PROMOTER.STATISTICS.TABLE.VERIFIED'), value: 'isVerified', sortable: true },
            { text: this.$t('PROMOTER.STATISTICS.TABLE.SINCE'), value: 'createdAt', sortable: true }
        ];
    }

    /**
     * Table rows with the sortable/searchable fields precomputed: the full
     * name, and a single status the chip and the column sort share.
     */
    public get driverRows() {
        if (!this.statistics) return [];
        return this.statistics.drivers.map(driver => ({
            ...driver,
            name: `${driver.firstName} ${driver.lastName}`.trim(),
            status: driver.isDeleted ? 'DELETED' : (driver.active ? 'ACTIVE' : 'INACTIVE')
        }));
    }

    public async created() {
        this.isLoading = true;
        try {
            this.statistics = await this.getStatistics(this.promoter!.id);
        } finally {
            this.isLoading = false;
        }
    }

    private get hasStatistics(): boolean {
        return !!this.statistics && (this.statistics.drivers.length > 0 || this.statistics.companies.length > 0);
    }

    public statusColor(status: string): string {
        switch (status) {
            case 'DELETED': return 'error';
            case 'INACTIVE': return 'warning';
            default: return 'success';
        }
    }

    public formatDate(value: string): string {
        return value ? moment(value).format('DD.MM.YYYY') : '—';
    }

    /**
     * The printed element is the live DOM, so a paginated table would print
     * only the visible page. Show all rows for the print, then restore.
     */
    private async printStatistics() {
        const previousItemsPerPage = this.itemsPerPage;
        this.itemsPerPage = -1;
        await this.$nextTick();
        const el = document.getElementById('print-format');
        if (el) {
            this.printer.print(el, [this.printStyle]);
        }
        this.itemsPerPage = previousItemsPerPage;
    }

    public dismiss(reloadData: boolean = false) {
        this.$emit('closeDialog', reloadData);
    }
}

